import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle2, Lock, FileText, Send, Loader2, Sparkles } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import api from "../lib/api";

const TEMPLATES = {
  accepted: `# ENROLLMENT ACCEPTANCE LETTER

**Date:** {{today}}
**To:** {{name}}

Dear {{first_name}},

Congratulations! We are pleased to inform you that you have successfully completed the {{track}} DirectHire enrollment program. Your dedication and commitment throughout this process have been exemplary.

**Next Steps:**

You are now confirmed for placement and onboarding as a **Payroll Assistant Trainee** with DirectHire. Our placement team will contact you within the next few business days to coordinate your start date and provide comprehensive onboarding information.

**What We Expect:**

As you transition into your new role, we expect all trainees to maintain the highest standards of professionalism, punctuality, and confidentiality.

Welcome to the DirectHire family, {{first_name}}.

Warm regards,

**The DirectHire Team**`,
  hold: `# ENROLLMENT — ON HOLD

**Date:** {{today}}
**To:** {{name}}

Dear {{first_name}},

Thank you for completing the {{track}} DirectHire enrollment program. After careful review, we'd like to place your candidacy on a brief hold while we finalise the next available placement window.

We will follow up within the next 2 weeks with a clear update.

Warm regards,

**The DirectHire Team**`,
  rejected: `# ENROLLMENT — REAPPLY NOTICE

**Date:** {{today}}
**To:** {{name}}

Dear {{first_name}},

Thank you for completing the {{track}} DirectHire enrollment program. After careful review, we are unable to extend a placement offer at this time. You are welcome to reapply in 6 months — we will gladly review your strengthened application then.

Warm regards,

**The DirectHire Team**`,
};

function applyTemplate(t, e) {
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const first = (e.full_name || "").split(" ")[0] || "Candidate";
  return t
    .replaceAll("{{today}}", today)
    .replaceAll("{{name}}", e.full_name || "")
    .replaceAll("{{first_name}}", first)
    .replaceAll("{{track}}", e.track || "14-day");
}

export default function EnrollmentDetailModal({ enrollmentId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("answers");
  const [decision, setDecision] = useState({ status: "accepted", title: "", letter: "" });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get(`/admin/enrollments/${enrollmentId}`);
        setData(r.data);
        if (r.data.decision) {
          setDecision({ status: r.data.decision.status, title: r.data.decision.title || "", letter: r.data.decision.letter || "" });
          setTab("decision");
        }
      } catch {
        toast.error("Failed to load enrollment");
      } finally { setLoading(false); }
    };
    load();
  }, [enrollmentId]);

  const useTemplate = () => {
    if (!data) return;
    const t = TEMPLATES[decision.status];
    setDecision((d) => ({ ...d, letter: applyTemplate(t, data) }));
  };

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/ai-letter`, { status: decision.status });
      setDecision((d) => ({ ...d, letter: r.data.letter }));
      toast.success("AI letter drafted — review and edit, then save.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI generation failed");
    } finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!decision.letter.trim()) { toast.error("Letter cannot be empty."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/enrollments/${enrollmentId}/decision`, decision);
      toast.success("Decision letter saved and visible to the candidate.");
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save decision");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear the decision letter? The candidate will no longer see it.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/enrollments/${enrollmentId}/decision`);
      toast.success("Cleared.");
      setDecision({ status: "accepted", title: "", letter: "" });
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="enrollment-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-navy/10">
          <div>
            <h2 className="font-serif text-xl text-navy" data-testid="modal-candidate-name">{loading ? "Loading…" : data?.full_name}</h2>
            <p className="text-xs text-navy/55">{data?.email} · {data?.track}</p>
          </div>
          <button onClick={onClose} className="text-navy/55 hover:text-navy" data-testid="modal-close-btn"><X className="h-5 w-5" /></button>
        </header>

        <div className="flex border-b border-navy/10 px-6 text-sm">
          <button onClick={() => setTab("answers")} className={`py-3 px-4 -mb-px border-b-2 ${tab === "answers" ? "border-[#10B981] text-navy font-semibold" : "border-transparent text-navy/55"}`} data-testid="modal-tab-answers">Answers (Day 1–{data?.total_days ?? "…"})</button>
          <button onClick={() => setTab("intake")} className={`py-3 px-4 -mb-px border-b-2 ${tab === "intake" ? "border-[#10B981] text-navy font-semibold" : "border-transparent text-navy/55"}`} data-testid="modal-tab-intake">Intake</button>
          <button onClick={() => setTab("decision")} className={`py-3 px-4 -mb-px border-b-2 ${tab === "decision" ? "border-[#10B981] text-navy font-semibold" : "border-transparent text-navy/55"}`} data-testid="modal-tab-decision">Decision Letter</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading && <div className="text-center py-12 text-navy/55"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}

          {!loading && tab === "answers" && (
            <div className="space-y-4" data-testid="modal-answers-list">
              {data?.responses?.length ? data.responses.map((r) => (
                <div key={r.day} className="border border-navy/10 rounded-lg p-4" data-testid={`answer-day-${r.day}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-[#10B981]">DAY {r.day}</span>
                    {r.answer ? <CheckCircle2 className="h-3.5 w-3.5 text-[#10B981]" /> : <Lock className="h-3.5 w-3.5 text-navy/30" />}
                    <span className="font-semibold text-navy">{r.title}</span>
                  </div>
                  <p className="text-sm text-navy/65 italic mb-2">{r.question}</p>
                  {r.answer ? (
                    <div className="bg-navy/[0.02] border border-navy/5 rounded p-3 text-sm text-navy/85 whitespace-pre-wrap leading-relaxed">{r.answer}</div>
                  ) : (
                    <div className="text-xs text-navy/40">Not yet submitted</div>
                  )}
                  {r.answered_at && <div className="mt-2 text-[11px] text-navy/40">Submitted {new Date(r.answered_at).toLocaleString()}</div>}
                </div>
              )) : <div className="text-center text-navy/55 py-8">No responses yet.</div>}
            </div>
          )}

          {!loading && tab === "intake" && (
            <div data-testid="modal-intake-view">
              {data?.intake ? (
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  {Object.entries(data.intake).map(([k, v]) => (
                    <div key={k} className="border-b border-navy/5 pb-2">
                      <dt className="text-xs uppercase text-navy/45 tracking-wide">{k.replaceAll("_", " ")}</dt>
                      <dd className="mt-0.5 text-navy">{typeof v === "boolean" ? (v ? "Yes" : "No") : (v || "—")}</dd>
                    </div>
                  ))}
                </dl>
              ) : <div className="text-center text-navy/55 py-8">No intake form submitted (legacy enrollment).</div>}
            </div>
          )}

          {!loading && tab === "decision" && (
            <div className="space-y-5" data-testid="modal-decision-form">
              {data?.decision && (
                <div className="rounded-md bg-[#10B981]/8 border border-[#10B981]/20 p-3 text-xs text-navy/70">
                  Current decision: <strong className="capitalize">{data.decision.status}</strong> · issued {new Date(data.decision.issued_at).toLocaleString()}
                </div>
              )}
              <div>
                <Label>Decision</Label>
                <div className="mt-2 flex gap-2">
                  {["accepted", "hold", "rejected"].map((s) => (
                    <button key={s} type="button" onClick={() => setDecision((d) => ({ ...d, status: s }))} className={`rounded-md px-4 py-2 text-sm font-medium ring-1 capitalize ${decision.status === s ? "bg-[#10B981] text-white ring-[#10B981]" : "bg-white text-navy ring-navy/20"}`} data-testid={`decision-status-${s}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="dtitle">Status badge label (optional)</Label>
                <Input id="dtitle" className="mt-2" placeholder='e.g., "Hired", "On Hold"' value={decision.title} onChange={(e) => setDecision({ ...decision, title: e.target.value })} data-testid="decision-title-input" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <Label htmlFor="dletter">Letter content (visible to candidate in portal)</Label>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={generateAI} disabled={aiBusy} className="text-xs text-[#0F2A1F] font-medium hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="decision-ai-generate-btn">
                      {aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      {aiBusy ? "Generating…" : "Generate with AI"}
                    </button>
                    <button type="button" onClick={useTemplate} className="text-xs text-[#10B981] font-medium hover:underline inline-flex items-center gap-1" data-testid="decision-use-template-btn">
                      <FileText className="h-3 w-3" /> Use template
                    </button>
                  </div>
                </div>
                <Textarea id="dletter" rows={14} className="font-mono text-sm" value={decision.letter} onChange={(e) => setDecision({ ...decision, letter: e.target.value })} placeholder="Type or paste the letter the candidate will see…" data-testid="decision-letter-input" />
              </div>
              <div className="flex items-center justify-between gap-3">
                {data?.decision && (
                  <button onClick={clear} disabled={busy} className="text-sm text-[#8b3a3a] hover:underline" data-testid="decision-clear-btn">Clear decision</button>
                )}
                <button onClick={save} disabled={busy} className="ml-auto rounded-md bg-[#0F2A1F] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#163b2c] disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="decision-save-btn">
                  <Send className="h-4 w-4" /> {busy ? "Saving…" : "Save & publish to portal"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
